export default function ConsequencesSection() {
  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
            The True Cost of Foreclosure
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Understanding what you're facing can motivate you to take action now. Here's what happens if you don't avoid foreclosure and let the process complete.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <img 
              src="https://public.readdy.ai/ai/img_res/fe3e0cbdb833c90e736f93cda2ea1dba.jpg" 
              alt="Credit damage from foreclosure" 
              className="w-full h-auto rounded-2xl shadow-xl"
            />
          </div>

          <div>
            <h3 className="text-3xl font-black text-gray-900 mb-6">Credit Score Devastation</h3>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              A foreclosure can drop your credit score by 200-400 points and remain on your credit report for seven years. This makes it extremely difficult to:
            </p>
            <ul className="space-y-4 mb-8">
              <li className="flex items-start">
                <i className="ri-close-circle-fill text-red-600 mr-3 mt-1 text-xl flex-shrink-0"></i>
                <span className="text-base text-gray-700">Buy another home (you'll need to wait 3-7 years for most loan programs)</span>
              </li>
              <li className="flex items-start">
                <i className="ri-close-circle-fill text-red-600 mr-3 mt-1 text-xl flex-shrink-0"></i>
                <span className="text-base text-gray-700">Rent an apartment (many landlords reject applicants with foreclosures)</span>
              </li>
              <li className="flex items-start">
                <i className="ri-close-circle-fill text-red-600 mr-3 mt-1 text-xl flex-shrink-0"></i>
                <span className="text-base text-gray-700">Get approved for credit cards or loans at reasonable interest rates</span>
              </li>
              <li className="flex items-start">
                <i className="ri-close-circle-fill text-red-600 mr-3 mt-1 text-xl flex-shrink-0"></i>
                <span className="text-base text-gray-700">Pass employment background checks (some employers check credit)</span>
              </li>
            </ul>
            <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded-lg">
              <p className="text-base text-gray-800 font-semibold mb-2">
                <i className="ri-lightbulb-fill text-teal-600 mr-2"></i>
                Avoid Foreclosure = Protect Your Credit
              </p>
              <p className="text-base text-gray-700 leading-relaxed">
                Selling your home before foreclosure has minimal impact on your credit compared to a completed foreclosure. We can help you avoid foreclosure and preserve your financial future.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 mb-16">
          <h3 className="text-3xl font-black text-gray-900 mb-8 text-center">Additional Consequences of Foreclosure</h3>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="flex items-start">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-money-dollar-circle-fill text-2xl text-red-600"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Deficiency Judgment</h4>
                  <p className="text-base text-gray-700 leading-relaxed">
                    If your home sells for less than you owe at the foreclosure auction, your lender can sue you for the difference. This deficiency judgment can follow you for years and result in wage garnishment.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-file-damage-fill text-2xl text-red-600"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Tax Consequences</h4>
                  <p className="text-base text-gray-700 leading-relaxed">
                    Forgiven debt from foreclosure may be considered taxable income by the IRS. You could receive a 1099-C form and owe taxes on the cancelled debt amount, adding financial burden to an already difficult situation.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-home-2-fill text-2xl text-red-600"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Forced Eviction</h4>
                  <p className="text-base text-gray-700 leading-relaxed">
                    After the foreclosure sale, you'll receive an eviction notice. If you don't leave voluntarily, the sheriff will forcibly remove you and your belongings. This is traumatic and can happen with little notice.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="flex items-start">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-emotion-sad-fill text-2xl text-red-600"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Emotional Stress</h4>
                  <p className="text-base text-gray-700 leading-relaxed">
                    The foreclosure process is emotionally devastating for families. The stress, shame, and uncertainty can affect your mental health, relationships, and overall well-being. Choosing to avoid foreclosure by selling can provide relief.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-briefcase-fill text-2xl text-red-600"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Employment Impact</h4>
                  <p className="text-base text-gray-700 leading-relaxed">
                    Some employers, especially in financial services, government, or positions requiring security clearance, may view foreclosure negatively. It can affect job opportunities and career advancement.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-community-fill text-2xl text-red-600"></i>
                </div>
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Public Record</h4>
                  <p className="text-base text-gray-700 leading-relaxed">
                    Foreclosure is a matter of public record in El Paso County. Your neighbors, friends, and community will be able to see that your home was foreclosed. Avoid foreclosure to maintain your privacy and dignity.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="relative rounded-2xl overflow-hidden">
          <img 
            src="https://readdy.ai/api/search-image?query=happy-family-moving-into-new-home-carrying-boxes-smiling-and-relieved-bright-sunny-day-fresh-start-concept-realistic-photography-positive-emotions-showing-life-after-avoiding-foreclosure-warm-welcoming-atmosphere&width=1400&height=600&seq=avoid-foreclosure-fresh-start-006&orientation=landscape" 
            alt="Fresh start after avoiding foreclosure" 
            className="w-full h-auto"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-teal-900/90 to-teal-900/60 flex items-center">
            <div className="max-w-2xl mx-auto px-8 text-center">
              <h3 className="text-3xl md:text-4xl font-black text-white mb-6">
                Choose a Better Path Forward
              </h3>
              <p className="text-lg text-gray-200 mb-8 leading-relaxed">
                You don't have to face these consequences. Selling your El Paso home to us helps you avoid foreclosure, protect your credit, and move forward with cash in hand and peace of mind.
              </p>
              <a 
                href="#how-we-help" 
                className="inline-block bg-white hover:bg-gray-100 text-gray-900 px-10 py-4 rounded-lg text-lg font-bold transition-all shadow-2xl cursor-pointer whitespace-nowrap"
              >
                See How We Can Help
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}